import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SyncClockService } from './sync-clock.service';

const DB_NAME = 'guardtracker';

// append to the list; dont make any changes to existing objects once deployed
const UPGRADE_STATEMENTS = [
  {
    toVersion: 1,
    statements: [
      `CREATE TABLE IF NOT EXISTS cached_items (
        id TEXT NOT NULL,
        type TEXT NOT NULL,
        data TEXT NOT NULL,
        subscriber_user_id TEXT NOT NULL,
        createdAt INTEGER,
        updatedAt INTEGER,
        PRIMARY KEY (id, type, subscriber_user_id)
      );`,
      `CREATE INDEX IF NOT EXISTS idx_cached_items_type ON cached_items(type);`,

      `CREATE TABLE IF NOT EXISTS saved_updates (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        eventId TEXT NOT NULL,
        type TEXT NOT NULL,
        data TEXT NOT NULL,
        subscriber_user_id TEXT NOT NULL,
        status INTEGER NOT NULL DEFAULT 1,
        retryResponse TEXT,
        createdAt INTEGER,
        updatedAt INTEGER
      );`,
      `CREATE INDEX IF NOT EXISTS idx_saved_updates_job ON saved_updates(eventId);`,
      `CREATE INDEX IF NOT EXISTS idx_saved_updates_status ON saved_updates(status);`,
      `CREATE INDEX IF NOT EXISTS idx_saved_updates_subscriber_user_id ON saved_updates(subscriber_user_id);`,
    ],
  },
  // Example for a future migration - always append, never edit the above:
  // {
  //   toVersion: 2,
  //   statements: [`ALTER TABLE saved_updates ADD COLUMN priority INTEGER DEFAULT 0;`],
  // },
];
type SqlParams = any[];
type BatchItem = string | [string, SqlParams];
export class TxBatch {
  readonly items: BatchItem[] = [];
  add(sql: string, params: SqlParams = []) {
    this.items.push([sql, params]);
  }
}

@Injectable({
  providedIn: 'root',
})
export class SqliteDbService {
  private db: any;
  private ready!: Promise<void>;
  private resolveReady!: () => void;
  private rejectReady!: (error: any) => void;

  private subscriberUserId: string = '';

  constructor(private clockService: SyncClockService) {
    this.ready = new Promise((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
    this.ready.catch(() => {});
    this.init();
  }

  whenReady() {
    return this.ready;
  }
  sleep(ms = 1000) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  private async waitForPlugin(timeOutMs = 10000) {
    const start = Date.now();

    while (!(window as any).sqlitePlugin) {
      if (Date.now() - start > timeOutMs) {
        throw new Error('Plugin not availble');
      }
      await new Promise((r) => setTimeout(r, 100));
    }
    return (window as any).sqlitePlugin;
  }

  private openDb(plugin: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const db = plugin.openDatabase(
        {
          name: DB_NAME,
          location: 'default',
        },
        () => resolve(db),
        (err: any) => reject(err),
      );
    });
  }

  async init() {
    if (!Capacitor.isNativePlatform()) {
      return; // Stop execution here to avoid the jeep-sqlite DOM error
    }

    try {
      const plugin = await this.waitForPlugin();
      console.log(plugin, 'sefsfrdfdf');

      this.db = await this.openDb(plugin);
      await this.migrate();

      this.resolveReady();
    } catch (err) {
      console.error('DatabaseService init failed', err);
      this.rejectReady(err);
      throw err;
    }
  }
  private async migrate() {
    const rows = await this.rawQuery('PRAGMA user_version');
    const current: number = rows?.[0]?.user_version || 0;
    console.log(current, 'prgma user version');

    const pending = UPGRADE_STATEMENTS.filter(
      (m: any) => m?.toVersion > current,
    ).sort((a, b) => a.toVersion - b.toVersion);

    for (const m of pending) {
      await this.batch(m.statements);
      await this.rawExec(`PRAGMA user_version = ${Number(m.toVersion)}`);
    }
  }
  private rawExec(sql: string, params: SqlParams = []): Promise<any> {
    return new Promise((resolve, reject) => {
      this.db.executeSql(sql, params, resolve, (err: any) => reject(err));
    });
  }
  private async rawQuery(sql: string, params: SqlParams = []): Promise<any[]> {
    const rs = await this.rawExec(sql, params);
    const out: any[] = [];
    for (let i = 0; i < rs.rows.length; i++) {
      out.push(rs.rows.item(i));
    }
    return out;
  }
  private batch(items: BatchItem[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.db.sqlBatch(
        items,
        () => resolve(),
        (err: any) => reject(err),
      );
    });
  }

  async execute(statement: string, values: any) {
    await this.whenReady();
    await this.rawExec(statement, values);
  }

  async query(statement: string, values: any) {
    await this.whenReady();
    return await this.rawQuery(statement, values ?? []);
  }

  async saveCachedItem(type: string, id: string, details: any) {
    await this.upsert(type, id, details);
  }

  async getCachedItemRow(type: string, id: string) {
    const row = await this.getRowFromCachedItem(type, id);
    return row ? (JSON.parse(row?.data) as any) : null;
  }
  async deleteCachedItemRow(type: string, id: string) {
    await this.removeCachedItemRow(type, id);

    return;
  }

  private async removeCachedItemRow(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    await this.execute(
      `DELETE FROM cached_items WHERE type = ? AND id = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );
  }

  private async getRowFromCachedItem(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    const rows = await this.query(
      `SELECT * FROM cached_items WHERE type = ? AND id = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );
    return rows[0] ?? null;
  }

  private async upsert(type: string, id: string, value: unknown) {
    const now = this.clockService.nowMs();
    const data = JSON.stringify(value);
    const subscriberUserId = this.getCurrentSubscriberUserId();

    await this.execute(
      `INSERT INTO cached_items (id, type, data, subscriber_user_id, createdAt, updatedAt)
       VALUES (?, ?, ?, ?, ?, ?)
       ON CONFLICT(id, type, subscriber_user_id) DO UPDATE SET data = excluded.data, updatedAt = excluded.updatedAt`,
      [id, type, data, subscriberUserId, now, now],
    );
  }

  async insertSavedUpdate(type: string, id: string, items: any) {
    await this.addSavedUpdate(type, id, items);
  }
  async getSavedUpdateRow(type: string, eventId: string) {
    return await this.getRowFromSavedUpdate(type, eventId);
  }

  async deleteSavedUpdateRow(type: string, id: string) {
    await this.removeSavedItemRow(type, id);
    return;
  }

  async getAllSavedUpdatesByStatuses(statuses: Array<number>) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    const placeholders = statuses.map(() => '?').join(', ');
    const rows = await this.query(
      `SELECT * FROM saved_updates WHERE status IN (${placeholders}) AND subscriber_user_id = ?`,
      [...statuses, subscriberUserId],
    );

    return rows || [];
  }

  private async removeSavedItemRow(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    await this.execute(
      `DELETE FROM saved_updates WHERE type = ? AND id = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );
  }
  private async getRowFromSavedUpdate(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    const rows = await this.query(
      `SELECT * FROM saved_updates WHERE type = ? AND eventId = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );

    return rows || [];
  }

  private async addSavedUpdate(type: string, id: string, value: unknown) {
    const now = this.clockService.nowMs();
    const data = JSON.stringify(value);

    const subscriberUserId = this.getCurrentSubscriberUserId();

    await this.execute(
      `INSERT INTO saved_updates (eventId, type, data, status, subscriber_user_id, retryResponse,  createdAt, updatedAt)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, type, data, 1, subscriberUserId, '', now, now],
    );
  }
  private getCurrentSubscriberUserId() {
    if (!this.subscriberUserId) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');

      this.subscriberUserId = userData?.subscriber?.subscriber_user_id;
    }
    return this.subscriberUserId;
  }
  resetCurrentSubscriberUserId() {
    this.subscriberUserId = '';
    this.getCurrentSubscriberUserId();
  }
}
